// [목적] /me/folios 내 폴리오 작업 공간 — 실데이터 무한스크롤 + visibility 서버 필터 + 카드 메뉴(공개 전환/삭제)
// [이유] FEED-06 — useFolioFeed(author=myHandle) 로 본인 폴리오 실데이터 연결.
//         ProtectedRoute(requireOnboarded) 래핑 — routes.tsx 에서 보장.
//         M7 P1: visibility 필터(all/public/private) → useFolioFeed(visibility) 서버 쿼리로 교체
//         (BE FoliosService.list author==='me' 분기가 M6 P2 로 이미 visibility 필터를 지원).
//         B5: 카드 "⋯" 메뉴 — 공개/링크 공개/비공개 전환(PATCH /folios/:id) + 삭제 확인 모달(DELETE /folios/:id).
//         낙관적 갱신은 react-query 캐시 대신 로컬 오버레이(pendingVisibility/pendingDeleted) 로 구현 —
//         무한스크롤 캐시(여러 탭 키) 를 직접 조작하는 것보다 단순하고 실패 롤백이 명확하다(ponytail).
// [사이드 이팩트] useAuth().user.handle 로 author 파라미터 전달 — handle 없으면 enabled=false.
//                 IntersectionObserver 무한스크롤 — hasNextPage=false 시 종료.
//                 filter!=='all' 일 때 통계(전체/공개/비공개 카운트)용 보조 쿼리 1건 추가 발생(useFolioFeed 캐시 분리).
// [수정 시 고려사항] like/bookmark/comment onClick 실배선 금지 (후속 fe-folio-social-wire).
//                   Settings me-* 엔드포인트 연결 금지 (후속 fe-settings-wire).
//                   [G1-2, 2026-09-17] useFolioFeed error 미처리(무한 로딩/빈 상태 오인) 수정 —
//                   InlineErrorState(재시도) 렌더. useFolioFeed 훅에 refetch 추가.
//                   pendingVisibility/pendingDeleted 오버레이는 컴포넌트 언마운트 시 소멸(세션 로컬) —
//                   성공 후 invalidateQueries 로 서버 진실값이 반영되면 자연히 덮어써진다.
//                   [2026-09-20 folio-templates] 빈 상태에 템플릿 피커(빈 폴리오/여행 코스/축제 후기) 추가 —
//                   기존 CTA(/chat AI 생성) 는 그대로 두고, 직접 작성 경로로 POST /folios 를 호출한다.
//                   템플릿 정의는 constants/folioTemplates.ts 단일 소스 — 이 파일에 프리셋을 복제하지 말 것.

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TopBar } from '../components/layout/TopBar.js';
import { FolioCardSkeleton } from '../components/feedback/Skeleton.js';
import { InlineErrorState } from '../components/feedback/InlineErrorState.js';
import { useToast } from '../components/feedback/Toast.js';
import { useShell } from '../hooks/useShell.js';
import { useAuth } from '../hooks/useAuth.js';
import { useFolioFeed } from '../hooks/useFolioFeed.js';
import { patchFolio, deleteFolio } from '../api/folioClient.js';
import {
  FolioTemplatePicker,
  FolioTemplateDialog,
} from '../components/folio/FolioTemplatePicker.js';
import { useCreateFolioFromTemplate } from '../hooks/useCreateFolioFromTemplate.js';
import type { FolioTemplate, FolioTemplateId } from '../constants/folioTemplates.js';
import { getMyLiked, getMyForked, getMyBookmarks, getMyBadgeCatalog } from '../api/user.client.js';
import { queryKeys } from '../api/queryKeys.js';
import { BadgeMedal } from '../components/badges/BadgeMedal.js';
import type { Folio } from '@gangwoncanvas/types';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const D_BORDER = c.line.base[L];
const D_BORDER_SOFT = c.line.soft[L];
const D_PAPER2 = c.paper[2][L];
// [folio-templates] 신규 추가 요소는 hex 직입(#fff) 대신 paper[0] 토큰 사용(CLAUDE §6-3)
const D_PAPER0 = c.paper[0][L];
const BRAND = c.brand[500][L];
const GREEN = c.accent[500][L];

// 모바일 추가 토큰
const TEXT_HI = c.text.high[L];
const TEXT_MID = c.text.mid[L];
const M_BG1 = c.surface[1][L];
const M_BORDER = c.line.base[L];

type Filter = 'all' | 'public' | 'private';

const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
  { id: 'all', label: '전체' },
  { id: 'public', label: '공개' },
  { id: 'private', label: '비공개' },
];

// [수정: 디자인 개편] 네 개 탭(만든 것·저장함·좋아요·가져온 것) — 백엔드에 이미 있는
// GET /users/me/{liked,forked,bookmarks} 3종 API 를 그대로 매핑한다(새 API 신설 없음).
// [사이드 이팩트] mine 외 3탭은 무한스크롤 없이 첫 페이지(limit 20)만 보여준다(범위 축소) —
// mine 탭만 기존 무한스크롤 + 수정/삭제 메뉴를 그대로 유지한다(소유권이 보장된 탭이므로).
// [수정 시 고려사항] 저장함/좋아요/가져온 것은 남의 폴리오일 수 있어 편집·삭제 메뉴를 보여주지 않는다
// (ReadOnlyFolioCard 사용). 무한스크롤이 필요해지면 각 API 의 cursor 페이지네이션을 이용할 것.
type MainTab = 'mine' | 'saved' | 'liked' | 'forked';

const MAIN_TABS: ReadonlyArray<{ id: MainTab; label: string }> = [
  { id: 'mine', label: '만든 것' },
  { id: 'saved', label: '저장함' },
  { id: 'liked', label: '좋아요' },
  { id: 'forked', label: '가져온 것' },
];

/**
 * [목적] mine 외 3탭(저장함/좋아요/가져온 것) 공통 데이터 훅 — 각자 다른 API 를 동일한 패턴으로 호출
 * [이유] 세 API 모두 FolioList(items: Folio[]) 를 동일하게 반환해 하나의 훅으로 묶을 수 있다.
 * [사이드 이팩트] 없음(조회 전용). enabled=false 이면 요청하지 않음.
 * [수정 시 고려사항] limit 고정 20 — 후속에 무한스크롤 도입 시 cursor 기반으로 교체.
 */
function useSimpleFolioTab(tab: 'saved' | 'liked' | 'forked', enabled: boolean) {
  return useQuery({
    queryKey: ['me', tab, 'simple', 20],
    queryFn: () => {
      if (tab === 'saved') return getMyBookmarks({ limit: 20 });
      if (tab === 'liked') return getMyLiked({ limit: 20 });
      return getMyForked({ limit: 20 });
    },
    enabled,
    staleTime: 1000 * 30,
  });
}

const VISIBILITY_LABEL: Record<Folio['visibility'], string> = {
  public: '공개',
  link: '링크 공개',
  private: '비공개',
};

// ─── useFolioActions ──────────────────────────────────────────────────────────

/**
 * [목적] 카드 메뉴 액션(공개 범위 전환/삭제) 공용 훅 — 낙관적 로컬 오버레이 + 실패 롤백
 * [이유] B5 — DesktopFolioCard/MobileFolioRow 양쪽이 동일 mutation·오버레이 상태를 공유.
 * [사이드 이팩트] patchFolio/deleteFolio(folioClient.ts, 읽기 전용 재사용) 호출. 성공 시
 *               folio-list 전체 캐시 invalidate(모든 visibility 탭 재조회)로 서버 진실값 동기화.
 * [수정 시 고려사항] pendingVisibility/pendingDeleted 는 컴포넌트 로컬 state — 페이지 이탈 시 소멸(재조회로 대체).
 */
function useFolioActions(): {
  pendingVisibility: Record<string, Folio['visibility']>;
  pendingDeleted: Set<string>;
  changeVisibility: (id: string, next: Folio['visibility']) => void;
  requestDelete: (id: string) => void;
  isPatching: boolean;
  isDeleting: boolean;
  deleteTargetId: string | null;
  confirmDelete: () => void;
  cancelDelete: () => void;
} {
  const queryClient = useQueryClient();
  const { show } = useToast();
  const [pendingVisibility, setPendingVisibility] = useState<Record<string, Folio['visibility']>>(
    {},
  );
  const [pendingDeleted, setPendingDeleted] = useState<Set<string>>(new Set());
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const patchMutation = useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: Folio['visibility'] }) =>
      patchFolio(id, { visibility }),
    onError: (_err, vars) => {
      // 롤백 — 오버레이 제거로 원래 값 복원
      setPendingVisibility((prev) => {
        const next = { ...prev };
        delete next[vars.id];
        return next;
      });
      show('공개 범위 변경에 실패했습니다.', 'error');
    },
    onSuccess: (_data, vars) => {
      show(`${VISIBILITY_LABEL[vars.visibility]}(으)로 전환했습니다.`, 'success');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['folio-list'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFolio(id),
    onError: (_err, id) => {
      setPendingDeleted((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      show('삭제에 실패했습니다.', 'error');
    },
    onSuccess: () => {
      show('폴리오를 삭제했습니다.', 'success');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['folio-list'] });
    },
  });

  return {
    pendingVisibility,
    pendingDeleted,
    changeVisibility: (id, next) => {
      setPendingVisibility((prev) => ({ ...prev, [id]: next }));
      patchMutation.mutate({ id, visibility: next });
    },
    requestDelete: (id) => setDeleteTargetId(id),
    isPatching: patchMutation.isPending,
    isDeleting: deleteMutation.isPending,
    deleteTargetId,
    confirmDelete: () => {
      if (!deleteTargetId) return;
      setPendingDeleted((prev) => new Set(prev).add(deleteTargetId));
      deleteMutation.mutate(deleteTargetId);
      setDeleteTargetId(null);
    },
    cancelDelete: () => setDeleteTargetId(null),
  };
}

/**
 * [목적] 낙관적 오버레이(변경된 visibility/삭제 예정) 를 서버 목록에 반영해 렌더용 배열 생성
 * [이유] react-query 캐시를 직접 조작하지 않고 컴포넌트 레벨에서 즉시 반영(useFolioActions 관례).
 * [사이드 이팩트] 없음(순수 함수) — filter!=='all' 이면 오버레이된 visibility 가 현재 탭과 달라진 항목은 숨김.
 * [수정 시 고려사항] filter==='all' 이면 항목은 유지하되 visibility 필드만 갱신 표시.
 */
function applyOptimisticOverlay(
  folios: Folio[],
  filter: Filter,
  pendingVisibility: Record<string, Folio['visibility']>,
  pendingDeleted: Set<string>,
): Folio[] {
  return folios
    .filter((f) => !pendingDeleted.has(f.id))
    .filter((f) => {
      const pv = pendingVisibility[f.id];
      if (!pv || filter === 'all') return true;
      return pv === filter;
    })
    .map((f) => (pendingVisibility[f.id] ? { ...f, visibility: pendingVisibility[f.id]! } : f));
}

// ─── FolioActionsMenu ─────────────────────────────────────────────────────────

/**
 * [목적] 카드 "⋯" 메뉴 — 공개/링크 공개/비공개 전환(현재 상태 제외) + 삭제
 * [이유] B5 — DesktopFolioCard/MobileFolioRow 공용 메뉴 컴포넌트.
 * [사이드 이팩트] 없음(호출부 콜백에 위임).
 * [수정 시 고려사항] 삭제는 즉시 실행하지 않고 onRequestDelete 로 확인 모달 오픈만 트리거.
 */
function FolioActionsMenu({
  folio,
  onChangeVisibility,
  onRequestDelete,
}: {
  folio: Folio;
  onChangeVisibility: (visibility: Folio['visibility']) => void;
  onRequestDelete: () => void;
}): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const otherVisibilities = (['public', 'link', 'private'] as const).filter(
    (v) => v !== folio.visibility,
  );

  return (
    <>
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        aria-label={`${folio.title} 더 보기`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: D_PAPER2,
          color: INK_HI,
          fontSize: 15,
          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
        }}
      >
        ⋯
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {otherVisibilities.map((v) => (
          <MenuItem
            key={v}
            onClick={() => {
              setAnchor(null);
              onChangeVisibility(v);
            }}
          >
            {`${VISIBILITY_LABEL[v]}(으)로 전환`}
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onRequestDelete();
          }}
          sx={{ color: c.danger[500][L] }}
        >
          삭제
        </MenuItem>
      </Menu>
    </>
  );
}

// ─── DeleteConfirmDialog ──────────────────────────────────────────────────────

/**
 * [목적] 폴리오 삭제 확인 모달 — Esc/취소로 닫힘, 확인 시 onConfirm 호출
 * [이유] B5 — 파괴적 액션(DELETE)은 즉시 실행 대신 명시적 확인 단계 필수(WCAG/UX 안전장치).
 * [수정 시 고려사항] MUI Dialog 가 Esc/focus trap 을 기본 제공.
 */
function DeleteConfirmDialog({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <Dialog open={open} onClose={onCancel} aria-labelledby="delete-folio-title">
      <DialogTitle id="delete-folio-title">폴리오를 삭제할까요?</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: INK_MID }}>
          삭제한 폴리오는 복구할 수 없습니다.
        </Typography>
      </DialogContent>
      <DialogActions>
        <ButtonBase
          onClick={onCancel}
          aria-label="삭제 취소"
          sx={{ px: '14px', py: '8px', fontSize: 13, color: INK_MID }}
        >
          취소
        </ButtonBase>
        <ButtonBase
          onClick={onConfirm}
          aria-label="삭제 확인"
          sx={{
            px: '14px',
            py: '8px',
            fontSize: 13,
            fontWeight: 700,
            color: c.danger[500][L],
          }}
        >
          삭제
        </ButtonBase>
      </DialogActions>
    </Dialog>
  );
}

// ─── BadgeStrip ───────────────────────────────────────────────────────────────

/**
 * [목적] 내가 획득한 배지를 알약(pill) 한 줄로 보여주는 공용 섹션 — 데스크톱은 줄바꿈, 모바일은 가로 스크롤
 * [이유] 배지 섹션이 데스크톱에만 있던 것을 모바일에도 제공. 두 레이아웃이 같은 쿼리키·같은 마크업을 쓰도록 한 곳에 둔다.
 *        [2026-09-21 badge-catalog] 라벨/등급을 FE 표(utils/badgeKind.ts 이모지 맵)에서 들고 있으면 BE 와 어긋나므로
 *        도감 API(GET /users/me/badges/catalog)의 earned 항목만 골라 쓴다 — 라벨은 BE, 메달 모양은 BadgeMedal.
 * [사이드 이팩트] GET /users/me/badges/catalog 조회(로그인 사용자만, staleTime 60s). 획득 배지가 0개거나
 *                 미로그인이면 아무것도 렌더하지 않는다. 프로필 뱃지 탭과 같은 쿼리 키라 캐시를 공유한다.
 * [수정 시 고려사항] 새 배지 kind 는 BE badge-definitions.ts + utils/badgeCatalog.ts 아이콘 맵에만 추가하면 된다.
 */
function BadgeStrip({ scroll = false }: { scroll?: boolean }): JSX.Element | null {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: queryKeys.user.badgeCatalog('me'),
    queryFn: () => getMyBadgeCatalog(),
    enabled: Boolean(user),
    staleTime: 1000 * 60,
  });
  const earned = (data?.items ?? []).filter((b) => b.earned).slice(0, 8);
  if (earned.length === 0) return null;
  return (
    <Box
      role="list"
      aria-label="획득한 배지"
      sx={{
        display: 'flex',
        gap: '8px',
        flexWrap: scroll ? 'nowrap' : 'wrap',
        overflowX: scroll ? 'auto' : 'visible',
        pb: scroll ? '12px' : 0,
        mb: scroll ? 0 : `${tokens.spacing[4]}px`,
      }}
    >
      {earned.map((b) => (
        <Box
          key={b.kind}
          role="listitem"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: '7px',
            bgcolor: c.paper[0][L],
            border: `1px solid ${D_BORDER}`,
            borderRadius: 99,
            pl: '7px',
            pr: '12px',
            py: '5px',
            fontSize: 11.5,
            color: INK_HI,
            fontWeight: 600,
          }}
        >
          <BadgeMedal kind={b.kind} tier={b.tier} size={22} />
          {b.label}
        </Box>
      ))}
    </Box>
  );
}

// ─── MyFoliosPage ─────────────────────────────────────────────────────────────

/**
 * [목적] /me/folios 내 폴리오 페이지 진입점 — 모바일/데스크톱 분기
 * [이유] FEED-06 실데이터 연결 — ProtectedRoute 로 인증 보장됨
 * [사이드 이팩트] useAuth().user.handle 로 author 파라미터 결정
 * [수정 시 고려사항] routes.tsx 에서 ProtectedRoute(requireOnboarded) 래핑 확인
 */
export function MyFoliosPage(): JSX.Element {
  const { viewport } = useShell();
  return viewport.isMobile ? <MyFoliosMobile /> : <MyFoliosDesktop />;
}

// ─── MyFoliosDesktop ──────────────────────────────────────────────────────────

/**
 * [목적] 템플릿 생성 UI 상태(모달 open + 진행 중 템플릿 id) 묶음 — 데스크톱/모바일 공유
 * [이유] 두 레이아웃이 같은 파일 안에 별도 컴포넌트로 구현돼 있어 상태 복제를 막기 위함.
 * [사이드 이팩트] select() 호출 시 POST /folios → 성공하면 useCreateFolioFromTemplate 가 편집 화면으로 이동시킨다.
 * [수정 시 고려사항] 진행 중(isPending) 에는 선택을 무시해 중복 생성(POST 2회) 을 막는다.
 */
function useTemplateCreation() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingId, setPendingId] = useState<FolioTemplateId | null>(null);
  const mutation = useCreateFolioFromTemplate();

  const select = (t: FolioTemplate) => {
    if (mutation.isPending) return;
    setPendingId(t.id);
    mutation.mutate(t, { onSettled: () => setPendingId(null) });
  };

  return {
    dialogOpen,
    openDialog: () => setDialogOpen(true),
    closeDialog: () => setDialogOpen(false),
    select,
    pending: mutation.isPending,
    pendingId,
  };
}

/**
 * [목적] 내 폴리오 데스크톱 레이아웃 — 헤더 + 필터 + 카드 그리드 무한스크롤
 * [이유] FEED-06 — useFolioFeed(author) 로 실데이터 렌더링
 * [사이드 이팩트] visibility 필터는 useFolioFeed(visibility) 서버 쿼리로 적용(M7 P1) — 클라이언트 필터 제거.
 * [수정 시 고려사항] author=undefined 이면 useFolioFeed enabled=false — handle 로드 전 요청 차단
 */
function MyFoliosDesktop(): JSX.Element {
  const navigate = useNavigate();
  const { viewport, openSidebar } = useShell();
  const { user } = useAuth();
  const [filter, setFilter] = useState<Filter>('all');
  // [수정: 디자인 개편] 만든 것/저장함/좋아요/가져온 것 4개 탭
  const [mainTab, setMainTab] = useState<MainTab>('mine');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const cols = viewport.isDesktop ? 3 : 2;
  const actions = useFolioActions();
  const templates = useTemplateCreation();
  const simpleTab = useSimpleFolioTab(
    mainTab === 'mine' ? 'saved' : mainTab,
    mainTab !== 'mine' && Boolean(user),
  );

  // [수정] author='me' 리터럴 전달 — BE folios.list 는 author==='me' 일 때만 본인 folio 를
  //        visibility 필터 없이(private·AI 생성 폴리오 포함) 반환한다. 기존엔 실제 handle 을
  //        넘겨 BE 가 인식하지 못해 public folio 만 와서 private AI 폴리오가 "내 폴리오"에 누락됐음.
  // M7 P1: filter(all/public/private) → visibility 서버 쿼리 파라미터로 직결(BE M6 P2 지원, 클라이언트 필터 제거)
  const { allFolios, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error, refetch } =
    useFolioFeed({
      author: 'me',
      visibility: filter === 'all' ? undefined : filter,
      limit: 20,
      enabled: Boolean(user),
    });

  // filter!=='all' 일 때는 현재 목록이 이미 한쪽 visibility 만 담고 있어 전체/공개/비공개 통계를
  // 낼 수 없다 — 통계 전용 보조 쿼리(항상 visibility 미지정) 를 별도로 조회한다(ponytail: 추가 요청 1건).
  const { allFolios: statsFolios } = useFolioFeed({
    author: 'me',
    limit: 20,
    enabled: Boolean(user) && filter !== 'all',
  });
  const statsSource = filter === 'all' ? allFolios : statsFolios;
  const totalCount = statsSource.length;
  const publicCount = statsSource.filter((f) => f.visibility === 'public').length;
  const privateCount = statsSource.filter((f) => f.visibility === 'private').length;

  const filtered = applyOptimisticOverlay(
    allFolios,
    filter,
    actions.pendingVisibility,
    actions.pendingDeleted,
  );

  /**
   * [목적] 무한 스크롤 sentinel IntersectionObserver
   * [이유] 스크롤 하단 도달 시 자동 fetchNextPage
   * [사이드 이팩트] hasNextPage/isFetchingNextPage 의존
   * [수정 시 고려사항] filter 변경은 fetchNextPage 에 영향 없음 — 클라이언트 필터만 적용
   */
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <TopBar
        title="내 폴리오"
        breadcrumb="MY WORKSPACE"
        mobile={viewport.isMobile}
        onBurger={openSidebar}
      />
      <Box
        sx={{
          p: {
            xs: `${tokens.spacing[6]}px ${tokens.spacing[4]}px 60px`,
            md: `${tokens.spacing[8]}px ${tokens.spacing[8]}px 60px`,
          },
          maxWidth: 1280,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* 헤더 */}
        <Box
          component="section"
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            mb: `${tokens.spacing[6]}px`,
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                color: INK_LOW,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                letterSpacing: '0.5px',
                mb: '6px',
              }}
            >
              MY FOLIOS · {isLoading ? '…' : totalCount} total
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: 28,
                fontWeight: 800,
                color: INK_HI,
                letterSpacing: '-0.7px',
                m: 0,
                mb: '8px',
                lineHeight: 1.2,
              }}
            >
              내 폴리오 작업 공간
            </Typography>
            {!isLoading && (
              <Stack direction="row" spacing="14px" sx={{ fontSize: 12, color: INK_MID }}>
                <Box component="span">
                  전체{' '}
                  <Box
                    component="strong"
                    sx={{ color: INK_HI, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                  >
                    {totalCount}
                  </Box>
                </Box>
                <Box component="span">
                  공개{' '}
                  <Box
                    component="strong"
                    sx={{ color: GREEN, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                  >
                    {publicCount}
                  </Box>
                </Box>
                <Box component="span">
                  비공개{' '}
                  <Box
                    component="strong"
                    sx={{ color: INK_MID, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                  >
                    {privateCount}
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>
          {/* [folio-templates] AI 생성(기본) + 템플릿 직접 작성(보조) 두 가지 생성 경로 */}
          <Stack direction="row" spacing={`${tokens.spacing[2]}px`} sx={{ flexShrink: 0 }}>
            <ButtonBase
              onClick={templates.openDialog}
              aria-label="템플릿으로 시작"
              sx={{
                bgcolor: 'transparent',
                color: INK_MID,
                border: `1px solid ${D_BORDER}`,
                borderRadius: `${tokens.radius.s}px`,
                px: '16px',
                py: '11px',
                fontSize: 13,
                fontWeight: 600,
                '&:hover': { borderColor: BRAND, color: BRAND },
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              템플릿으로 시작
            </ButtonBase>
            <ButtonBase
              onClick={() => navigate('/chat')}
              aria-label="새 폴리오 만들기"
              sx={{
                bgcolor: BRAND,
                color: '#fff',
                borderRadius: `${tokens.radius.s}px`,
                px: '18px',
                py: '11px',
                fontSize: 13,
                fontWeight: 700,
                gap: '7px',
                boxShadow: `0 4px 12px ${BRAND}40`,
                '&:hover': { opacity: 0.88 },
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              <Box component="span" aria-hidden="true">
                ✦
              </Box>{' '}
              새 폴리오 만들기
            </ButtonBase>
          </Stack>
        </Box>

        {/* [수정: 디자인 개편] 배지 섹션 — GET /users/me/badges 실데이터(모바일과 BadgeStrip 공유) */}
        <BadgeStrip />

        {/* [수정: 디자인 개편] 다른 사람 폴리오에서 가져오기 진입점 */}
        <ButtonBase
          onClick={() => navigate('/explore')}
          aria-label="남의 폴리오에서 최근 가져오기"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: 12,
            color: BRAND,
            fontWeight: 600,
            mb: `${tokens.spacing[5]}px`,
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          🔍 남의 폴리오에서 최근 가져오기 →
        </ButtonBase>

        {/* [수정: 디자인 개편] 만든 것/저장함/좋아요/가져온 것 탭 */}
        <Box
          role="tablist"
          aria-label="내 폴리오 분류"
          sx={{
            display: 'flex',
            gap: '4px',
            mb: `${tokens.spacing[4]}px`,
            borderBottom: `1px solid ${D_BORDER}`,
          }}
        >
          {MAIN_TABS.map((t) => {
            const active = mainTab === t.id;
            return (
              <ButtonBase
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setMainTab(t.id)}
                sx={{
                  px: '14px',
                  py: '10px',
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? INK_HI : INK_MID,
                  borderBottom: `2px solid ${active ? BRAND : 'transparent'}`,
                  mb: '-1px',
                }}
              >
                {t.label}
              </ButtonBase>
            );
          })}
        </Box>

        {mainTab !== 'mine' ? (
          <>
            {simpleTab.isLoading ? (
              <Box
                sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '16px' }}
              >
                {[...Array(cols * 2)].map((_, i) => (
                  <FolioCardSkeleton key={i} />
                ))}
              </Box>
            ) : simpleTab.error ? (
              <InlineErrorState message="목록을 불러오지 못했습니다." onRetry={simpleTab.refetch} />
            ) : (simpleTab.data?.items.length ?? 0) === 0 ? (
              <Box sx={{ textAlign: 'center', p: '80px 20px', color: INK_LOW }}>
                <Box aria-hidden="true" sx={{ fontSize: 48, mb: '14px', opacity: 0.4 }}>
                  ❑
                </Box>
                <Typography sx={{ fontSize: 13, color: INK_MID, lineHeight: 1.6 }}>
                  {mainTab === 'saved' && '저장한 폴리오가 없어요.'}
                  {mainTab === 'liked' && '좋아요한 폴리오가 없어요.'}
                  {mainTab === 'forked' &&
                    '가져온 폴리오가 없어요. 발견에서 마음에 드는 폴리오를 Fork해보세요.'}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '16px' }}
              >
                {(simpleTab.data?.items ?? []).map((f) => (
                  <ReadOnlyFolioCard
                    key={f.id}
                    folio={f}
                    onOpen={() => navigate(`/folio/${f.id}`)}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <>
            {/* 필터 */}
            <Box
              role="group"
              aria-label="폴리오 필터"
              sx={{ display: 'flex', gap: '6px', mb: `${tokens.spacing[5]}px` }}
            >
              {FILTERS.map((f) => {
                const active = filter === f.id;
                return (
                  <ButtonBase
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    aria-label={`필터: ${f.label}`}
                    aria-pressed={active}
                    sx={{
                      bgcolor: active ? INK_HI : '#fff',
                      color: active ? '#fff' : INK_MID,
                      border: `1px solid ${active ? INK_HI : D_BORDER}`,
                      borderRadius: 99,
                      px: '13px',
                      py: '6px',
                      fontSize: 11.5,
                      fontWeight: 600,
                      '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                    }}
                  >
                    {f.label}
                  </ButtonBase>
                );
              })}
            </Box>

            {/* 카드 그리드 — [G1-2] error 시 무한 로딩/빈 상태 오인 대신 InlineErrorState(재시도) 우선 렌더 */}
            {error ? (
              <InlineErrorState message="폴리오 목록을 불러오지 못했습니다." onRetry={refetch} />
            ) : isLoading ? (
              <Box
                sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '16px' }}
              >
                {[...Array(cols * 2)].map((_, i) => (
                  <FolioCardSkeleton key={i} />
                ))}
              </Box>
            ) : filtered.length === 0 ? (
              /* [folio-templates] 빈 상태 — 안내문만 두는 대신 바로 시작할 수 있는 템플릿 3종을 노출 */
              <Box sx={{ textAlign: 'center', p: '56px 20px 40px', color: INK_LOW }}>
                <Box aria-hidden="true" sx={{ fontSize: 48, mb: '14px', opacity: 0.4 }}>
                  ❑
                </Box>
                <Typography sx={{ fontSize: 13, color: INK_MID, lineHeight: 1.6 }}>
                  {filter === 'all'
                    ? '아직 폴리오가 없어요. 템플릿으로 바로 시작해 보세요.'
                    : '해당 필터에 폴리오가 없어요.'}
                </Typography>
                {filter === 'all' && (
                  <Box sx={{ maxWidth: 720, mx: 'auto', mt: `${tokens.spacing[6]}px` }}>
                    <FolioTemplatePicker
                      onSelect={templates.select}
                      pending={templates.pending}
                      pendingId={templates.pendingId}
                      columns={3}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '16px' }}
              >
                {filtered.map((f) => (
                  <DesktopFolioCard
                    key={f.id}
                    folio={f}
                    onOpen={() => navigate(`/folio/${f.id}`)}
                    onEdit={() => navigate(`/folio/${f.id}/edit`)}
                    onChangeVisibility={(v) => actions.changeVisibility(f.id, v)}
                    onRequestDelete={() => actions.requestDelete(f.id)}
                  />
                ))}
              </Box>
            )}

            {/* 무한 스크롤 sentinel */}
            <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden="true" />

            {/* 페이지 로딩 */}
            {isFetchingNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: '20px' }}>
                <CircularProgress size={24} aria-label="더 불러오는 중" />
              </Box>
            )}

            {/* 목록 끝 */}
            {!hasNextPage && filtered.length > 0 && !isLoading && (
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: 11,
                  color: INK_LOW,
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  py: '16px',
                }}
              >
                — 전체 {filtered.length}개 —
              </Typography>
            )}
          </>
        )}
      </Box>
      <DeleteConfirmDialog
        open={actions.deleteTargetId !== null}
        onCancel={actions.cancelDelete}
        onConfirm={actions.confirmDelete}
      />
      <FolioTemplateDialog
        open={templates.dialogOpen}
        onClose={templates.closeDialog}
        onSelect={templates.select}
        pending={templates.pending}
        pendingId={templates.pendingId}
        columns={3}
      />
    </>
  );
}

// ─── DesktopFolioCard ─────────────────────────────────────────────────────────

/**
 * [목적] 내 폴리오 데스크톱 카드 — 커버 + 제목 + 공개/비공개 배지 + 편집/보기 버튼
 * [이유] FEED-06 — 실데이터(Folio) 기반 렌더링
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] coverImageUrl 없으면 PAPER2 배경 폴백
 */
/**
 * [목적] 저장함/좋아요/가져온 것 탭용 간단 카드 — 남의 폴리오일 수 있어 편집/삭제/공개전환 메뉴를 보여주지 않는다.
 * [이유] 이 3탭(저장함/좋아요/가져온 것)은 본인이 소유하지 않은 폴리오도 포함할 수 있어(저장/좋아요),
 *        DesktopFolioCard의 수정/삭제 메뉴를 그대로 쓰면 소유권 없는 폴리오에서 403 오류가 날 수 있다.
 * [사이드 이팩트] 없음(읽기 전용).
 */
function ReadOnlyFolioCard({ folio, onOpen }: { folio: Folio; onOpen: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={`폴리오 ${folio.title} 열기`}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        bgcolor: '#fff',
        borderRadius: `${tokens.radius.l}px`,
        border: `1px solid ${D_BORDER}`,
        overflow: 'hidden',
        boxShadow: tokens.shadow.card,
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          height: 160,
          bgcolor: D_PAPER2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 56,
          overflow: 'hidden',
        }}
      >
        {folio.coverImageUrl ? (
          <Box
            component="img"
            src={folio.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🗺'
        )}
      </Box>
      <Box sx={{ p: '14px 16px' }}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: INK_HI,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: '4px',
          }}
        >
          {folio.title}
        </Typography>
        <Typography sx={{ fontSize: 11, color: INK_LOW }}>
          @{folio.author?.handle ?? '알 수 없음'} · ♥ {folio.likeCount}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

function DesktopFolioCard({
  folio,
  onOpen,
  onEdit,
  onChangeVisibility,
  onRequestDelete,
}: {
  folio: Folio;
  onOpen: () => void;
  onEdit: () => void;
  onChangeVisibility: (visibility: Folio['visibility']) => void;
  onRequestDelete: () => void;
}): JSX.Element {
  const isPublic = folio.visibility === 'public';
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: `${tokens.radius.l}px`,
        border: `1px solid ${D_BORDER}`,
        overflow: 'hidden',
        boxShadow: tokens.shadow.card,
        transition: `transform ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}, box-shadow ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        '&:hover': { transform: 'translateY(-2px)', boxShadow: tokens.shadow.modal },
      }}
    >
      <ButtonBase
        onClick={onOpen}
        aria-label={`폴리오 ${folio.title} 열기`}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
        }}
      >
        {/* 커버 */}
        <Box
          sx={{
            height: 160,
            bgcolor: D_PAPER2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 56,
            position: 'relative',
            overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          {folio.coverImageUrl ? (
            <Box
              component="img"
              src={folio.coverImageUrl}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            '🗺'
          )}
          {folio.duration.days && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                bgcolor: 'rgba(255,255,255,.95)',
                borderRadius: `${tokens.radius.s}px`,
                px: '8px',
                py: '3px',
                fontSize: 10,
                color: INK_HI,
                fontWeight: 700,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
            >
              {folio.duration.days}일
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              bgcolor: isPublic ? `${GREEN}F0` : 'rgba(255,255,255,.92)',
              color: isPublic ? '#fff' : INK_MID,
              borderRadius: `${tokens.radius.s}px`,
              px: '8px',
              py: '3px',
              fontSize: 10,
              fontWeight: 700,
            }}
            aria-label={VISIBILITY_LABEL[folio.visibility]}
          >
            {VISIBILITY_LABEL[folio.visibility]}
          </Box>
        </Box>

        <Box sx={{ p: '14px 16px 4px' }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: INK_HI,
              lineHeight: 1.4,
              mb: '6px',
              letterSpacing: '-0.2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {folio.title}
          </Typography>
          <Box
            component="span"
            aria-label={`좋아요 ${folio.likeCount}개`}
            sx={{
              fontSize: 11,
              color: INK_LOW,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            ♡ {folio.likeCount} likes
          </Box>
        </Box>
      </ButtonBase>

      {/* 액션 버튼 */}
      <Box
        sx={{
          display: 'flex',
          gap: '6px',
          p: '10px 16px 14px',
          borderTop: `1px solid ${D_BORDER_SOFT}`,
          mt: '10px',
        }}
      >
        <ButtonBase
          onClick={onEdit}
          aria-label={`${folio.title} 편집`}
          sx={{
            flex: 1,
            bgcolor: BRAND,
            color: '#fff',
            borderRadius: `${tokens.radius.s}px`,
            py: '7px',
            fontSize: 12,
            fontWeight: 700,
            '&:hover': { opacity: 0.88 },
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          ✎ 편집
        </ButtonBase>
        <ButtonBase
          onClick={onOpen}
          aria-label={`${folio.title} 보기`}
          sx={{
            flex: 1,
            bgcolor: D_PAPER2,
            color: INK_HI,
            borderRadius: `${tokens.radius.s}px`,
            py: '7px',
            fontSize: 12,
            fontWeight: 600,
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          보기
        </ButtonBase>
        <FolioActionsMenu
          folio={folio}
          onChangeVisibility={onChangeVisibility}
          onRequestDelete={onRequestDelete}
        />
      </Box>
    </Box>
  );
}

// ─── MyFoliosMobile ───────────────────────────────────────────────────────────

/**
 * [목적] 내 폴리오 모바일 레이아웃 — 헤더 + 필터 + 행 리스트 무한스크롤
 * [이유] FEED-06 모바일 실데이터 연결
 * [사이드 이팩트] 동일 useFolioFeed(author) 사용 — 캐시 공유
 * [수정 시 고려사항] 모바일 sentinelRef 는 별도 ref 로 관리
 */
function MyFoliosMobile(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filter, setFilter] = useState<Filter>('all');
  // [수정: 디자인 개편] 만든 것/저장함/좋아요/가져온 것 탭 — 데스크톱과 동일한 개념
  const [mainTab, setMainTab] = useState<MainTab>('mine');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const actions = useFolioActions();
  const templates = useTemplateCreation();
  const simpleTab = useSimpleFolioTab(
    mainTab === 'mine' ? 'saved' : mainTab,
    mainTab !== 'mine' && Boolean(user),
  );

  // [수정] author='me' 리터럴 전달 — BE folios.list 는 author==='me' 일 때만 본인 folio 를
  //        visibility 필터 없이(private·AI 생성 폴리오 포함) 반환한다. 기존엔 실제 handle 을
  //        넘겨 BE 가 인식하지 못해 public folio 만 와서 private AI 폴리오가 "내 폴리오"에 누락됐음.
  // M7 P1: filter → visibility 서버 쿼리 파라미터 직결(데스크톱과 동일, 클라이언트 필터 제거)
  const { allFolios, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error, refetch } =
    useFolioFeed({
      author: 'me',
      visibility: filter === 'all' ? undefined : filter,
      limit: 20,
      enabled: Boolean(user),
    });

  const { allFolios: statsFolios } = useFolioFeed({
    author: 'me',
    limit: 20,
    enabled: Boolean(user) && filter !== 'all',
  });
  const statsSource = filter === 'all' ? allFolios : statsFolios;
  const totalCount = statsSource.length;
  const publicCount = statsSource.filter((f) => f.visibility === 'public').length;

  const filtered = applyOptimisticOverlay(
    allFolios,
    filter,
    actions.pendingVisibility,
    actions.pendingDeleted,
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: M_BG1 }}>
      {/* 헤더 */}
      <Box sx={{ bgcolor: '#fff', p: '18px 20px 0', borderBottom: `1px solid ${M_BORDER}` }}>
        <Typography
          sx={{
            fontSize: 10,
            color: TEXT_MID,
            fontWeight: 500,
            letterSpacing: 1,
            textTransform: 'uppercase',
            mb: '4px',
          }}
        >
          MY WORKSPACE
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: TEXT_HI, mb: '6px' }}>
          내 폴리오
        </Typography>
        <Typography sx={{ fontSize: 12, color: TEXT_MID, mb: '14px' }}>
          전체 {isLoading ? '…' : totalCount}개 · 공개 {isLoading ? '…' : publicCount}개
        </Typography>

        {/* [수정: 디자인 개편] 배지 섹션 — 데스크톱과 같은 BadgeStrip, 모바일은 가로 스크롤 */}
        <BadgeStrip scroll />

        {/* [수정: 디자인 개편] 만든 것/저장함/좋아요/가져온 것 탭 */}
        <Box
          role="tablist"
          aria-label="내 폴리오 분류"
          sx={{ display: 'flex', gap: '4px', pb: '10px', overflowX: 'auto' }}
        >
          {MAIN_TABS.map((t) => {
            const active = mainTab === t.id;
            return (
              <ButtonBase
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setMainTab(t.id)}
                sx={{
                  flexShrink: 0,
                  px: '12px',
                  py: '8px',
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? TEXT_HI : TEXT_MID,
                  borderBottom: `2px solid ${active ? BRAND : 'transparent'}`,
                }}
              >
                {t.label}
              </ButtonBase>
            );
          })}
        </Box>

        {/* 필터 — mainTab==='mine' 일 때만 의미 있음 */}
        {mainTab === 'mine' && (
          <Box
            role="group"
            aria-label="폴리오 필터"
            sx={{ display: 'flex', gap: '6px', pb: '14px' }}
          >
            {FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <ButtonBase
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  aria-label={`필터: ${f.label}`}
                  aria-pressed={active}
                  sx={{
                    flexShrink: 0,
                    borderRadius: 20,
                    px: '12px',
                    py: '5px',
                    fontSize: 11,
                    border: `1px solid ${active ? BRAND : M_BORDER}`,
                    bgcolor: active ? `${BRAND}15` : '#fff',
                    color: active ? BRAND : TEXT_MID,
                    fontWeight: active ? 600 : 400,
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                  }}
                >
                  {f.label}
                </ButtonBase>
              );
            })}
          </Box>
        )}
      </Box>

      {/* 리스트 — [G1-2] error 시 InlineErrorState(재시도) 우선 렌더 */}
      <Box sx={{ p: '16px 20px 32px' }}>
        {mainTab !== 'mine' ? (
          simpleTab.isLoading ? (
            <Stack spacing="10px">
              {[...Array(3)].map((_, i) => (
                <FolioCardSkeleton key={i} />
              ))}
            </Stack>
          ) : simpleTab.error ? (
            <InlineErrorState message="목록을 불러오지 못했습니다." onRetry={simpleTab.refetch} />
          ) : (simpleTab.data?.items.length ?? 0) === 0 ? (
            <Box sx={{ textAlign: 'center', p: '60px 20px' }}>
              <Box aria-hidden="true" sx={{ fontSize: 36, mb: '12px', opacity: 0.4 }}>
                ❑
              </Box>
              <Typography sx={{ fontSize: 13, color: TEXT_MID }}>
                {mainTab === 'saved' && '저장한 폴리오가 없어요.'}
                {mainTab === 'liked' && '좋아요한 폴리오가 없어요.'}
                {mainTab === 'forked' && '가져온 폴리오가 없어요.'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing="10px">
              {(simpleTab.data?.items ?? []).map((f) => (
                <ReadOnlyFolioCard key={f.id} folio={f} onOpen={() => navigate(`/folio/${f.id}`)} />
              ))}
            </Stack>
          )
        ) : error ? (
          <InlineErrorState message="폴리오 목록을 불러오지 못했습니다." onRetry={refetch} />
        ) : isLoading ? (
          <Stack spacing="10px">
            {[...Array(4)].map((_, i) => (
              <FolioCardSkeleton key={i} />
            ))}
          </Stack>
        ) : filtered.length === 0 ? (
          /* [folio-templates] 모바일 빈 상태 — 1열 템플릿 카드로 바로 시작 */
          <Box sx={{ textAlign: 'center', p: '40px 4px 24px' }}>
            <Box aria-hidden="true" sx={{ fontSize: 36, mb: '12px', opacity: 0.4 }}>
              ❑
            </Box>
            <Typography sx={{ fontSize: 13, color: TEXT_MID }}>
              {filter === 'all'
                ? '아직 폴리오가 없어요. 템플릿으로 바로 시작해 보세요.'
                : '해당 필터에 폴리오가 없어요.'}
            </Typography>
            {filter === 'all' && (
              <Box sx={{ mt: `${tokens.spacing[5]}px` }}>
                <FolioTemplatePicker
                  onSelect={templates.select}
                  pending={templates.pending}
                  pendingId={templates.pendingId}
                  columns={1}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Stack spacing="10px">
            {filtered.map((f) => (
              <MobileFolioRow
                key={f.id}
                folio={f}
                onOpen={() => navigate(`/folio/${f.id}`)}
                onEdit={() => navigate(`/folio/${f.id}/edit`)}
                onChangeVisibility={(v) => actions.changeVisibility(f.id, v)}
                onRequestDelete={() => actions.requestDelete(f.id)}
              />
            ))}
            <ButtonBase
              onClick={() => navigate('/chat')}
              aria-label="새 폴리오 만들기"
              sx={{
                mt: '4px',
                p: '12px',
                bgcolor: '#fff',
                border: `1px dashed ${M_BORDER}`,
                borderRadius: `${tokens.radius.m}px`,
                fontSize: 13,
                color: BRAND,
                fontWeight: 500,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              + 새 폴리오 만들기
            </ButtonBase>
            {/* [folio-templates] 목록이 있는 사용자도 템플릿 진입점을 가질 수 있도록 모달 트리거 제공 */}
            <ButtonBase
              onClick={templates.openDialog}
              aria-label="템플릿으로 시작"
              sx={{
                p: '12px',
                bgcolor: D_PAPER0,
                border: `1px dashed ${M_BORDER}`,
                borderRadius: `${tokens.radius.m}px`,
                fontSize: 13,
                color: TEXT_MID,
                fontWeight: 500,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              템플릿으로 시작
            </ButtonBase>
          </Stack>
        )}

        {/* 무한 스크롤 sentinel */}
        <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden="true" />

        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '16px' }}>
            <CircularProgress size={20} aria-label="더 불러오는 중" />
          </Box>
        )}
      </Box>
      <DeleteConfirmDialog
        open={actions.deleteTargetId !== null}
        onCancel={actions.cancelDelete}
        onConfirm={actions.confirmDelete}
      />
      <FolioTemplateDialog
        open={templates.dialogOpen}
        onClose={templates.closeDialog}
        onSelect={templates.select}
        pending={templates.pending}
        pendingId={templates.pendingId}
        columns={1}
      />
    </Box>
  );
}

// ─── MobileFolioRow ────────────────────────────────────────────────────────────

/**
 * [목적] 내 폴리오 모바일 행 — 썸네일 + 제목 + 통계 + 편집 버튼
 * [이유] FEED-06 모바일 실데이터 기반 렌더링
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] coverImageUrl 없으면 PAPER2 배경 폴백
 */
function MobileFolioRow({
  folio,
  onOpen,
  onEdit,
  onChangeVisibility,
  onRequestDelete,
}: {
  folio: Folio;
  onOpen: () => void;
  onEdit: () => void;
  onChangeVisibility: (visibility: Folio['visibility']) => void;
  onRequestDelete: () => void;
}): JSX.Element {
  const isPublic = folio.visibility === 'public';
  return (
    <Box
      sx={{
        display: 'flex',
        bgcolor: '#fff',
        borderRadius: `${tokens.radius.m}px`,
        overflow: 'hidden',
        border: `1px solid ${M_BORDER}`,
        boxShadow: tokens.shadow.card,
      }}
    >
      <ButtonBase
        onClick={onOpen}
        aria-label={`폴리오 ${folio.title} 열기`}
        sx={{
          flex: 1,
          display: 'flex',
          gap: '10px',
          minWidth: 0,
          alignItems: 'stretch',
          textAlign: 'left',
          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
        }}
      >
        {/* 썸네일 */}
        <Box
          sx={{
            width: 64,
            bgcolor: D_PAPER2,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          {folio.coverImageUrl ? (
            <Box
              component="img"
              src={folio.coverImageUrl}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            '🗺'
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, p: '10px 0' }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: TEXT_HI,
              mb: '4px',
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {folio.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {folio.duration.days && (
              <Box component="span" sx={{ fontSize: 10, color: TEXT_MID }}>
                {folio.duration.days}일
              </Box>
            )}
            <Box
              component="span"
              aria-label={`좋아요 ${folio.likeCount}개`}
              sx={{ fontSize: 10, color: TEXT_MID }}
            >
              ♥ {folio.likeCount}
            </Box>
            <Box
              component="span"
              aria-label={VISIBILITY_LABEL[folio.visibility]}
              sx={{
                fontSize: 9,
                borderRadius: `${tokens.radius.s}px`,
                px: '6px',
                py: '1px',
                fontWeight: 600,
                bgcolor: isPublic ? `${GREEN}20` : M_BG1,
                color: isPublic ? GREEN : TEXT_MID,
              }}
            >
              {VISIBILITY_LABEL[folio.visibility]}
            </Box>
          </Box>
        </Box>
      </ButtonBase>

      <ButtonBase
        onClick={onEdit}
        aria-label={`${folio.title} 편집`}
        sx={{
          width: 56,
          bgcolor: `${BRAND}15`,
          color: BRAND,
          fontSize: 16,
          flexShrink: 0,
          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
        }}
      >
        ✎
      </ButtonBase>
      <FolioActionsMenu
        folio={folio}
        onChangeVisibility={onChangeVisibility}
        onRequestDelete={onRequestDelete}
      />
    </Box>
  );
}
